/**
 * Whether a workflow run is still plausibly executing.
 *
 * `status === 'running'` is NOT on its own evidence that a run is alive. The
 * agents runtime writes a run's terminal status from inside the very process
 * that executes its steps, so when that process dies mid-step nothing marks
 * the record — it simply stays `running` until the 7-day lifecycle deletes
 * it, and no server-side sweeper reconciles it.
 *
 * In prod on 2026-08-09 an 8-step run lost its AgentCore container 14 minutes
 * in. Three days later the record still read `running`, so the console
 * re-adopted it on every mount, polled it every 2.5s indefinitely, and showed
 * an undismissable RUNNING card with a 68-hour elapsed timer (dismissing
 * cleared only local state, so it returned on the next navigation).
 *
 * This mirrors `packages/runtime/src/workflows/liveness.ts` in the agents
 * repo — same rule, same bound, applied on both sides of the API. Keep them
 * in step; the record shape is a cross-repo contract.
 */

import type { WorkflowRunRecord } from './workflowsApi.ts';

/**
 * How long a `running` record may go unwritten before it is presumed
 * abandoned.
 *
 * The engine rewrites the record at every step transition, so the liveness
 * signal is the gap between writes — one agent turn plus its review pass.
 * Records predating `updatedAt` fall back to `initiatedAt`, so the bound must
 * also exceed a whole plausible RUN. Runs observed in prod complete in 9–13
 * minutes and cannot outlive the container hosting them, so 30 minutes leaves
 * better than 2x headroom on either basis.
 */
export const RUN_STALE_AFTER_MS = 30 * 60_000;

export type RunLiveness = 'live' | 'abandoned' | 'terminal';

/** What the record actually means, as opposed to the status it stores. */
export function runLiveness(record: WorkflowRunRecord, now: number = Date.now()): RunLiveness {
  if (record.status !== 'running') {
    return 'terminal';
  }
  const lastWrite = Date.parse(record.updatedAt ?? record.initiatedAt);
  // An unparseable timestamp is not evidence of life. Reading it as live is
  // precisely what produced the undismissable card, so it reads as abandoned.
  if (Number.isNaN(lastWrite)) {
    return 'abandoned';
  }
  return now - lastWrite < RUN_STALE_AFTER_MS ? 'live' : 'abandoned';
}
